import isNil from 'lodash/isNil';
import {useSelector} from 'react-redux';
import {useEffect, useRef, useState} from 'react';
import Arg from '@livongo/arg';
import AmpliUtils from '../utils/amplitude-utils';
import {TRACK_EVENT_KEYS} from '../constants/amplitude';
import Login from '../Login/Login';
import Loader from '../common/loader/Loader';
import SharedCoaching from '../utils/SharedCoaching';
import SharedCoachingSchedulingForm from '../SchedulingForm/SharedCoachingSchedulingForm';
import SchedulingFormMyStrength from '../SchedulingFormMyStrength/SchedulingFormMyStrength';
import SchedulingFormMentalHealth from '../SchedulingFormMentalHealth/SchedulingFormMentalHealth';
import SchedulingFormMentalHealthAxle from '../SchedulingFormMentalHealth/SchedulingFormMentalHealthAxle';
import SchedulingFormV2 from '../SchedulingForm/SchedulingFormV2';
import SchedulingFormAxle from '../SchedulingForm/SchedulingFormAxle';

const referrer = Arg('referrer') || 'livongo-web';
const referringProduct = referrer ? referrer.split('-')[0] : null;

const SchedulingPage = () => {
    const {token, uuid} = useSelector(state => state.userData.auth);
    const {enableAxleForm} = useSelector(state => state.userData.featureFlags);
    const hasTrackedLogin = useRef(false);
    const hasTrackedForm = useRef(false);
    const prevToken = useRef(null);
    const isOneApp = Arg('oneapp') || Arg('oneAppSdk');
    const [isSharedCoaching, setIsSharedCoaching] = useState(
        Arg('sharedCoaching')
    );

    useEffect(() => {
        const checkSharedCoaching = async () => {
            if (!isNil(isSharedCoaching)) {
                return;
            }

            // If there is no 'sharedCoaching' parameter and referringProduct is livongo, then coming from Galaxy link
            if (
                isNil(isSharedCoaching) &&
                referringProduct === 'livongo' &&
                token &&
                uuid
            ) {
                const coachingInfo = await SharedCoaching.coachingInfo(
                    token,
                    uuid
                );

                setIsSharedCoaching(coachingInfo?.sharedCoaching);
            }
        };

        checkSharedCoaching();
    }, [token, uuid, isSharedCoaching]);

    useEffect(() => {
        const wasNotLoggedIn = !prevToken.current;
        const isMobile = Arg('mobile');
        const referrerValue = Arg('referrer') || 'direct';

        // Track login page view - only when showing login
        if (!token && !hasTrackedLogin.current) {
            hasTrackedLogin.current = true;
            AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.COACHING_PAGE_VIEWED, {
                referrer: referrerValue,
                isMobile,
                isOneApp,
            });
        }

        // Track scheduling form view - only when user just logged in and form is shown
        if (token && !hasTrackedForm.current && wasNotLoggedIn) {
            hasTrackedForm.current = true;
            AmpliUtils.setTrackingData(
                TRACK_EVENT_KEYS.SCHEDULING_FORM_VIEWED,
                {
                    referrer: referrerValue,
                    isMobile,
                    isOneApp,
                }
            );
        }

        // Update previous token reference
        prevToken.current = token;
    }, [isOneApp, token]);

    // Once the token is set, useSelector detects the change and this component is
    // re-rendered with the new token value, so this condition will no longer be triggered
    if (referringProduct === 'livongo' && !token) {
        // If there's a uuid param and no token, App.js is still fetching the token
        if (Arg('u')) {
            return <Loader />;
        }

        return <Login />;
    }

    // Once the token is set, useSelector detects the change and this component is
    // re-rendered with the new token value, so this condition will no longer be triggered
    if (referringProduct === 'livongo' && !token) {
        // If there's a uuid param and no token, App.js is still fetching the token
        if (Arg('u')) {
            return <Loader />;
        }

        return <Login />;
    }

    return (
        <>
            {referringProduct === 'livongo' &&
                (isNil(isSharedCoaching) ? (
                    <Loader />
                ) : isSharedCoaching ? (
                    <SharedCoachingSchedulingForm />
                ) : enableAxleForm ? (
                    <SchedulingFormAxle />
                ) : (
                    <SchedulingFormV2 />
                ))}
            {referringProduct === 'mystrength' && <SchedulingFormMyStrength />}
            {referringProduct === 'mentalhealth' &&
                (enableAxleForm ? (
                    <SchedulingFormMentalHealthAxle />
                ) : (
                    <SchedulingFormMentalHealth />
                ))}
        </>
    );
};

export default SchedulingPage;
